import { useEffect, useRef } from "react";

// Make a full-screen overlay a place the BACK GESTURE can leave.
//
// ---------------------------------------------------------------------------
// THE BUG THIS EXISTS TO FIX
// ---------------------------------------------------------------------------
// App.jsx pushes a history entry when the merchant moves between the gallery
// and the camera. The two overlays — the gallery's photo viewer and the
// camera's shot inspector — were pure component state with no entry of their
// own. So the back gesture, which is how anyone actually leaves a full-screen
// image on a phone, skipped straight past them and popped the SCREEN instead:
//
//   in the gallery, opening a photo and pressing back landed on the CAMERA
//   in the camera, opening a shot and pressing back threw you out of the camera
//
// Both were reported from the shop floor, and both are the same missing piece.
// An overlay that covers the screen is a place as far as the person using it is
// concerned, so it has to be a place as far as history is concerned.
//
// ---------------------------------------------------------------------------
// WHY THE ENTRY IS TAKEN BACK OFF ON A BUTTON CLOSE
// ---------------------------------------------------------------------------
// There are two ways out — the back gesture, and the overlay's own close button
// — and they must leave the history stack in the same state. If a button close
// simply hid the overlay, our pushed entry would still be sitting there, and
// the merchant's next back gesture would consume it and appear to do nothing at
// all. So a button close calls history.back() to unwind its own entry.
//
// That call fires a popstate of its own, which would otherwise be read as "the
// merchant pressed back" and close an overlay that is already closed. The
// `unwinding` flag swallows exactly one.

/**
 * @param {boolean} isOpen   whether the overlay is on screen
 * @param {() => void} onClose  called when the back gesture should close it
 */
// Overlays can stack (the camera's take viewer, then the old-photo viewer on
// it). One back step must close only the TOP one, and a button close's own
// history.back() must be swallowed by every hook, so both are module-wide.
const stack = [];
let unwinds = 0;
let lastEvent = null;
let lastSwallowed = false;

export function useOverlayHistory(isOpen, onClose) {
  // A ref rather than state: none of this should cause a render, and both flags
  // are read inside listeners that must see the current value rather than the
  // one captured when they were attached.
  const ref = useRef({ pushed: false });

  useEffect(() => {
    const s = ref.current;

    if (isOpen && !s.pushed) {
      s.pushed = true;
      stack.push(s);
      // No URL change. The overlay is a state of the screen it sits on, not a
      // separate address, and rewriting the path would make a shared link open
      // something the sender did not mean to send.
      // Keep the host's own marker (App's __recap layer) on the entry.
      window.history.pushState({ ...(window.history.state || {}), __overlay: true }, "");
      return;
    }

    if (!isOpen && s.pushed) {
      s.pushed = false;
      const at = stack.indexOf(s);
      if (at >= 0) stack.splice(at, 1);
      unwinds += 1;
      window.history.back();
    }
  }, [isOpen]);

  useEffect(() => {
    // Registered unconditionally, NOT only while open: the popstate produced by
    // our own history.back() above arrives after the overlay has already gone,
    // so a listener that unmounted with the overlay would never see it and the
    // flag would stay set forever.
    const onPop = (e) => {
      const s = ref.current;
      // Every hook hears the same event; the first one decides if it is ours.
      if (lastEvent !== e) {
        lastEvent = e;
        lastSwallowed = unwinds > 0;
        if (lastSwallowed) unwinds -= 1;
      }
      if (lastSwallowed) return;
      if (s.pushed && stack[stack.length - 1] === s) {
        s.pushed = false;
        stack.pop();
        onClose();
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [onClose]);

  useEffect(() => () => {
    const at = stack.indexOf(ref.current);
    if (at >= 0) stack.splice(at, 1);
  }, []);
}

export default useOverlayHistory;
